import {
  type TwinProperty,
  type TwinPropertyValue,
} from "@shared/services/twinManagement";
import { defineStore } from "pinia";
import { ref } from "vue";

type TwinFieldValue = TwinPropertyValue | undefined;

export function normalizeTwinFieldValue(
  value: TwinPropertyValue | null | undefined,
): TwinFieldValue {
  return value ?? undefined;
}

type TwinField = {
  propertyUuid: string;
  propertyTemplateUuid?: string;
  name: string;
  value: TwinFieldValue;
  registrations: number;
};

export const useTwinFieldStore = defineStore("twinField", () => {
  const fieldsByTwinUuid = ref<Record<string, Record<string, TwinField>>>({});

  function updateField(property: TwinProperty, update: Partial<TwinField>) {
    const fields = fieldsByTwinUuid.value[property.twinUuid] ?? {};
    const field = fields[property.uuid];
    fieldsByTwinUuid.value = {
      ...fieldsByTwinUuid.value,
      [property.twinUuid]: {
        ...fields,
        [property.uuid]: {
          propertyUuid: property.uuid,
          propertyTemplateUuid: property.propertyTemplateUuid,
          name: property.name,
          value: property.propertyValue?.value,
          registrations: 0,
          ...field,
          ...update,
        },
      },
    };
  }

  function registerField(
    property: TwinProperty,
    value: TwinPropertyValue | null | undefined,
  ) {
    const field = fieldsByTwinUuid.value[property.twinUuid]?.[property.uuid];
    updateField(property, {
      value: normalizeTwinFieldValue(value),
      registrations: (field?.registrations ?? 0) + 1,
    });

    let registered = true;
    return () => {
      if (!registered) {
        return;
      }
      registered = false;

      const fields = fieldsByTwinUuid.value[property.twinUuid];
      const currentField = fields?.[property.uuid];
      if (!fields || !currentField) {
        return;
      }

      if (currentField.registrations > 1) {
        updateField(property, {
          registrations: currentField.registrations - 1,
        });
        return;
      }

      const remainingFields = { ...fields };
      delete remainingFields[property.uuid];
      if (Object.keys(remainingFields).length === 0) {
        const remainingTwins = { ...fieldsByTwinUuid.value };
        delete remainingTwins[property.twinUuid];
        fieldsByTwinUuid.value = remainingTwins;
        return;
      }
      fieldsByTwinUuid.value = {
        ...fieldsByTwinUuid.value,
        [property.twinUuid]: remainingFields,
      };
    };
  }

  function setFieldValue(
    property: TwinProperty,
    value: TwinPropertyValue | null | undefined,
  ) {
    if (!fieldsByTwinUuid.value[property.twinUuid]?.[property.uuid]) {
      return;
    }
    updateField(property, { value: normalizeTwinFieldValue(value) });
  }

  function getField(twinUuid: string, propertyUuid: string) {
    const fields = Object.entries(fieldsByTwinUuid.value).find(
      ([uuid]) => uuid === twinUuid,
    )?.[1];
    if (!fields) {
      return undefined;
    }
    return Object.values(fields).find(
      (field) =>
        field.propertyUuid === propertyUuid ||
        field.propertyTemplateUuid === propertyUuid,
    );
  }

  return {
    registerField,
    setFieldValue,
    getField,
  };
});
